import { useAutoAnimate } from '@formkit/auto-animate/react';
import { useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';

import { EventMutationTradeProps } from '../event-mutation';
import { EventCurrencySwap } from './event-currency-swap';
import { EventWarning } from './event-warning';
import { EventBuyMarketButtons } from './rows/event-buy-market-buttons';
import EventBuyProfitMultiplierRow from './rows/event-buy-profit-multiplier-row';
import EventBuyProfitRow from './rows/event-buy-profit-row';
import { EventBuyReceive } from './rows/event-buy-receive';

import { WalletBalance } from '@/components/common/balance/wallet-balance';
import { UsdcIcon } from '@/components/common/icons/usdc';
import { Skeleton } from '@/components/ui/skeleton';
import { DEFAULT_MIN_SIZE } from '@/constants/default-values';
import {
  ORDER_OUTCOME,
  ORDER_TYPE,
  PRICE_DECIMALS,
  TOKEN_DECIMALS,
} from '@/constants/solana';
import { useFetchMarketState } from '@/hooks/solana/query/use-fetch-market-state';
import { useFetchApproxExchangeRate } from '@/hooks/solana/query/use-fetch-quote';
import {
  calculateSimpleModeShares,
  useSimpleModeShares,
} from '@/hooks/ui/use-simple-mode-shares';
import { bumpBuy, roundSharesAmount } from '@/lib/orderbook';
import { mapOrderbook, calculateMarketOrderFill, cn } from '@/lib/utils';
import { useOrderbookStore } from '@/store/orderbook';
import { useUIModeStore } from '@/store/ui-mode';

export const EventMutationBuyMarket = ({
  outcome,
  setOutcome,
  id,
  setShares,
  shares,
  orderbookData,
  resetTrigger,
  onAmountUsdChange,
  fallbackYes,
  fallbackNo,
  yes,
  isHighlighted,
  no,
  isQuickBet = false,
}: EventMutationTradeProps & {
  orderbookData: ReturnType<typeof mapOrderbook>;
}) => {
  const [tokenAmount, setTokenAmount] = useState('');

  useEffect(() => {
    if (resetTrigger && resetTrigger > 0) setTokenAmount('');
  }, [resetTrigger]);

  const [debouncedTokenAmount] = useDebounce(tokenAmount, 1000);
  const { data: marketState } = useFetchMarketState(id);

  const minSize = useMemo(() => {
    if (!marketState) return DEFAULT_MIN_SIZE;
    return Number(marketState.minSize) / TOKEN_DECIMALS;
  }, [marketState]);

  const { isUsdc, setPrice } = useOrderbookStore();
  const { data: approxExchangeRate, isLoading: isApproxExchangeRateLoading } =
    useFetchApproxExchangeRate();
  const mode = useUIModeStore((s) => s.mode);

  useEffect(() => {
    const v = tokenAmount ? Number(tokenAmount) : 0;
    const usd = isUsdc ? v : v * (approxExchangeRate?.usdcPerSolMinimum || 0);
    onAmountUsdChange?.(usd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenAmount, isUsdc, approxExchangeRate?.usdcPerSolMinimum]);

  const baseCents =
    outcome === ORDER_OUTCOME.YES ? (fallbackYes ?? 50) : (fallbackNo ?? 50);
  const p = bumpBuy(baseCents);
  const priceUsd = p / 100;

  const price = outcome === ORDER_OUTCOME.YES ? yes : no;
  const simpleModeShares = useSimpleModeShares(
    price,
    minSize,
    tokenAmount,
    isUsdc ? undefined : approxExchangeRate?.usdcPerSolMinimum,
  );
  const uiShares = mode === 'simple' ? simpleModeShares.toString() : shares;

  const { averagePrice, totalShares, totalCost, nextBestPrice } =
    useMemo(() => {
      if (mode === 'simple') {
        const shares = calculateSimpleModeShares(
          price,
          minSize,
          isUsdc,
          tokenAmount,
          isUsdc ? undefined : approxExchangeRate?.usdcPerSolMinimum,
        );
        setShares(shares.toString());
      }
      if (!isUsdc) {
        if (!approxExchangeRate) {
          setShares('0');
          return {
            averagePrice: 0,
            totalShares: 0,
            totalCost: 0,
            nextBestPrice: null as number | null,
          };
        }

        const usdcAmount =
          (+tokenAmount || 0) * approxExchangeRate.usdcPerSolMinimum;

        const data = calculateMarketOrderFill(
          orderbookData,
          usdcAmount,
          ORDER_TYPE.BUY,
        );

        const tokenAmountUsd =
          (+tokenAmount || 0) * approxExchangeRate.usdcPerSolMinimum;

        const unfilledTotalCost = tokenAmountUsd - data.totalCost;
        if (mode === 'simple' && unfilledTotalCost > 0) {
          const unfilledPartPrice = priceUsd;

          const unfilledShares = unfilledTotalCost / unfilledPartPrice;
          const sumShares = unfilledShares + data.totalShares;
          const totalShares = roundSharesAmount(sumShares, priceUsd, minSize);
          // console.log('desired win = ', desiredWin);
          // console.log('price usd = ', priceUsd);

          // console.log('total cost = ', spendUsd);
          // console.log('next best price = ', p);
          // console.log('total shares = ', desiredWin);
          return {
            averagePrice: p,
            totalShares: totalShares,
            totalCost: +tokenAmountUsd,
            nextBestPrice: p,
          };
        }

        const totalShares = roundSharesAmount(
          data.totalShares,
          priceUsd,
          minSize,
        );
        if (mode !== 'simple') {
          setShares(totalShares.toString());
        }
        return { ...data, totalShares };
      }

      const amountUsd = +tokenAmount || 0;

      const data = calculateMarketOrderFill(
        orderbookData,
        amountUsd,
        ORDER_TYPE.BUY,
      );

      const unfilledTotalCost = +tokenAmount - data.totalCost;
      if (mode === 'simple' && unfilledTotalCost > 0) {
        const unfilledPartPrice = priceUsd;

        const unfilledShares = unfilledTotalCost / unfilledPartPrice;
        const sumShares = unfilledShares + data.totalShares;
        // console.log('desired win = ', desiredWin);
        // console.log('price usd = ', priceUsd);
        const totalShares = roundSharesAmount(sumShares, priceUsd, minSize);

        // console.log('total cost = ', spendUsd);
        // console.log('next best price = ', p);
        // console.log('total shares = ', desiredWin);
        return {
          averagePrice: p,
          totalShares: totalShares,
          totalCost: +tokenAmount,
          nextBestPrice: p,
        };
      }

      const totalShares = roundSharesAmount(
        data.totalShares,
        priceUsd,
        minSize,
      );

      if (mode !== 'simple') {
        setShares(totalShares.toString());
      }
      return { ...data, totalShares };
    }, [
      mode,
      isUsdc,
      tokenAmount,
      orderbookData,
      priceUsd,
      minSize,
      price,
      approxExchangeRate,
      setShares,
      p,
    ]);

  const headTick =
    orderbookData.asks?.[orderbookData.asks.length - 1]?.price ?? 0;
  const headShares =
    orderbookData.asks?.[orderbookData.asks.length - 1]?.size ?? 0;
  const headPriceUsd = headTick > 0 ? headTick / PRICE_DECIMALS : 0;
  const requestedUsd = isUsdc
    ? +tokenAmount || 0
    : (+tokenAmount || 0) * (approxExchangeRate?.usdcPerSolMinimum || 0);
  const EPS = 0.005;
  const willPlaceMarketAndLimit =
    mode === 'simple' &&
    headPriceUsd > 0 &&
    requestedUsd > 0 &&
    requestedUsd / headPriceUsd > headShares + EPS;
  const totalCostUI = willPlaceMarketAndLimit ? requestedUsd : totalCost;

  const handleChangeAmount = (value: string) => {
    if (value && !/^[0-9.,]+$/.test(value)) return;
    if (value.startsWith('.')) value = '0' + value;

    const num = parseFloat(value.replace(',', '.')) || 0;
    const amountUsd = isUsdc
      ? num
      : num * (approxExchangeRate?.usdcPerSolMinimum || 0);

    const { totalShares } = calculateMarketOrderFill(
      orderbookData,
      amountUsd,
      ORDER_TYPE.BUY,
    );

    setShares(roundSharesAmount(totalShares, priceUsd, minSize).toString());
    setTokenAmount(value.replace(',', '.'));
  };

  const [parent] = useAutoAnimate();

  const solAmount = useMemo(() => {
    if (isUsdc) return 0;
    return Number(
      (+totalCost / (approxExchangeRate?.usdcPerSol || 0)).toFixed(4),
    );
  }, [isUsdc, totalCost, approxExchangeRate?.usdcPerSol]);
  const bestOppositePriceCents =
    orderbookData.asks?.[orderbookData.asks.length - 1]?.price ?? 0;
  const showPartialFill =
    bestOppositePriceCents > 0 &&
    (isUsdc
      ? +tokenAmount > totalCost && totalCost > 0
      : +tokenAmount > solAmount &&
        solAmount > 0 &&
        (+tokenAmount - +solAmount) / +tokenAmount > 0.01);

  return (
    <>
      <div className="flex flex-row gap-[10px]">
        <div className="grow text-[14px] text-[#767676]">Amount</div>
        <input
          className="h-[40px] w-full bg-transparent text-end text-[36px]"
          value={tokenAmount}
          type="number"
          placeholder="0"
          onChange={(e) => handleChangeAmount(e.target.value)}
        />
        <EventCurrencySwap />
      </div>
      <div ref={parent} className="flex flex-col gap-4">
        <EventBuyMarketButtons
          setTokenAmount={setTokenAmount}
          tokenAmount={tokenAmount}
          setShares={setShares}
          orderbookData={orderbookData}
        />
        {mode !== 'simple' &&
          Number(tokenAmount) > 0 &&
          totalShares === 0 &&
          (isUsdc || (!isUsdc && !isApproxExchangeRateLoading)) &&
          debouncedTokenAmount === tokenAmount && (
            <EventWarning
              text="There are currently no matching orders on the other side. Try placing a limit order or check back shortly"
              showLongText
            />
          )}

        {mode !== 'simple' && showPartialFill && (
          <EventWarning
            title="Only part of your order can be filled."
            text={`Only ${isUsdc ? totalCost : solAmount} ${isUsdc ? 'USDC' : 'SOL'} are available to fill at market. You can place the remaining ${
              isUsdc
                ? +tokenAmount - totalCost
                : (+tokenAmount - solAmount).toFixed(4)
            } ${isUsdc ? 'USDC' : 'SOL'} as a limit order after this trade.`}
            showLongText
          />
        )}
      </div>
      {!isQuickBet && <WalletBalance />}
      {!isQuickBet && !isHighlighted && (
        <div className="border border-[#212121]" />
      )}
      <div className="flex flex-col gap-2">
        {/* <EventAveragePriceRow averageLimitPrice={averagePrice} /> */}
        <EventBuyProfitRow shares={uiShares} totalCost={totalCostUI} />
        {!isQuickBet && !isHighlighted && (
          <EventBuyProfitMultiplierRow
            shares={uiShares}
            totalCost={totalCostUI}
          />
        )}
      </div>

      {!isQuickBet && (
        <div
          className={cn(
            'border border-[#212121]',
            isHighlighted && 'max-lg:hidden',
          )}
        />
      )}
      <div>
        {mode !== 'simple' && (
          <EventBuyReceive
            marketId={id}
            price={null}
            receiveAmountWithoutFee={totalCostUI}
            matchingAmount={totalCostUI}
            nextBestPrice={nextBestPrice}
            solAmount={totalCostUI * (approxExchangeRate?.solPerUsdc || 0)}
            tradeType="buy"
            avgPrice={averagePrice}
          />
        )}

        <div className="flex flex-row">
          <div className="grow text-[20px] font-medium sm:text-[24px]">
            You Win
          </div>
          <div className="flex flex-row items-center justify-center gap-[10px] text-[20px] font-medium sm:text-[24px]">
            {isApproxExchangeRateLoading && !isUsdc ? (
              <Skeleton className="h-[24px] w-[100px] sm:h-[32px]" />
            ) : (
              `${(+uiShares).toFixed(2)}`
            )}
            <UsdcIcon className="size-[24px] sm:size-[32px]" />
          </div>
        </div>
      </div>
    </>
  );
};
